import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  CreateIdentityProviderConfigurationRequestInterface,
  GetIdentityProviderConfigurationResponse,
  IdentityProviderAdminApiService,
  IdentityProviderConfiguration,
  UpdateIdentityProviderConfigurationRequestMutationInterface,
} from '@vendasta/sso';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export interface OAuth2IdentityProviderConfig {
  authorizationEndpoint: string;
  tokenEndpoint: string;
  userinfoEndpoint: string;
}

export interface OpenIDIdentityProviderConfig {
  discoveryEndpoint: string;
}

export interface IdentityProviderConfig {
  partnerId: string;
  clientId: string;
  clientSecret?: string;
  integrationType: 'oauth2' | 'openid';
  oauth2?: OAuth2IdentityProviderConfig;
  openid?: OpenIDIdentityProviderConfig;
}

@Injectable({
  providedIn: 'root',
})
export class IdentityProviderConfigService {
  constructor(
    private idpAdminService: IdentityProviderAdminApiService,
    @Inject('PARTNER_ID') private partnerId$: Observable<string>,
  ) {}

  public getIdentityProviderConfig(): Observable<IdentityProviderConfig | null> {
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.idpAdminService.getIdentityProvider({ partnerId })),
      map((resp: GetIdentityProviderConfigurationResponse) => {
        if (!resp || !resp.configuration) {
          return null;
        }
        const respConfig: IdentityProviderConfiguration = resp.configuration;
        const config: IdentityProviderConfig = {
          partnerId: respConfig.partnerId || '',
          clientId: respConfig.clientId || '',
          integrationType: 'oauth2',
        };
        if (respConfig.openId) {
          config.integrationType = 'openid';
          config.openid = {
            discoveryEndpoint: respConfig.openId.discoveryEndpoint || '',
          };
        } else {
          config.integrationType = 'oauth2';
          config.oauth2 = {
            authorizationEndpoint: respConfig.oauth2.authorizationEndpoint || '',
            tokenEndpoint: respConfig.oauth2.tokenEndpoint || '',
            userinfoEndpoint: respConfig.oauth2.userinfoEndpoint || '',
          };
        }
        return config;
      }),
    );
  }

  public createIdentityProviderConfig(config: IdentityProviderConfig): Observable<HttpResponse<null>> {
    const req: CreateIdentityProviderConfigurationRequestInterface = {
      clientId: config.clientId,
      clientSecret: config.clientSecret,
    };
    switch (config.integrationType) {
      case 'oauth2':
        req.oauth2 = config.oauth2;
        break;
      case 'openid':
        req.openId = config.openid;
        break;
    }
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.idpAdminService.createIdentityProvider({ ...req, partnerId: partnerId })),
    );
  }

  public replaceIdentityProviderConfig(config: IdentityProviderConfig): Observable<HttpResponse<null>> {
    const mutations: UpdateIdentityProviderConfigurationRequestMutationInterface[] = [{ clientId: config.clientId }];
    if (config.clientSecret) {
      mutations.push({ clientSecret: config.clientSecret });
    }
    switch (config.integrationType) {
      case 'oauth2':
        mutations.push({ replaceOauth2: config.oauth2 });
        break;

      case 'openid':
        mutations.push({ replaceOpenId: config.openid });
        break;
    }
    return this.partnerId$.pipe(
      switchMap((partnerId) =>
        this.idpAdminService.updateIdentityProvider({ partnerId: partnerId, mutations: mutations }),
      ),
    );
  }

  public deleteConfig(): Observable<HttpResponse<null>> {
    return this.partnerId$.pipe(switchMap((partnerId) => this.idpAdminService.deleteIdentityProvider({ partnerId })));
  }
}
